import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useState } from 'react';
import * as React from 'react';
import { useContext } from 'react';

import { UserContext } from '../../context';
import type { ITheme } from '../../theme';

const useStyles = makeStyles()((theme: ITheme) => ({
  adminBox: {
    position: 'fixed',
    top: 16,
    right: 16,
    zIndex: 9999,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    borderRadius: 8,
    padding: theme.spacing(1),
    minWidth: 200,
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  adminTitle: {
    color: '#fff',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  urlItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5),
    borderRadius: 4,
    marginBottom: theme.spacing(0.25),
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  urlText: {
    color: '#fff',
    fontSize: '0.7rem',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    flex: 1,
  },
  copyButton: {
    color: '#fff',
    padding: theme.spacing(0.25),
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  toggleButton: {
    position: 'fixed',
    top: 16,
    right: 16,
    zIndex: 10000,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
  },
}));

interface AdminUrlBoxProps {
  className?: string;
}

const AdminUrlBox: React.FC<AdminUrlBoxProps> = ({ className }) => {
  const { classes } = useStyles();
  const { user } = useContext(UserContext);
  const [isExpanded, setIsExpanded] = useState(false);

  if (user?.role !== 'admin') {
    return null;
  }

  const urls = [
    { platform: 'YouTube MUSE Starlight', url: 'https://www.youtube.com/watch?v=Pgum6OT_VH8' },
    { platform: 'YouTube Violin', url: 'https://www.youtube.com/watch?v=mdkWFPrrZVQ' },
    { platform: 'YouTube MUSE Song', url: 'https://www.youtube.com/watch?v=Pgum6OT_VH8' },
    {
      platform: 'TikTok Guitar Vocals',
      url: 'https://www.tiktok.com/@larkinpoe/video/7532122550382415135',
    },
    { platform: 'Insta Bass', url: 'https://www.instagram.com/seiji_igusa/reel/DOjL5z3D-Wt/' },
    { platform: 'Insta Piano', url: 'https://www.instagram.com/p/C5G3A1ctzgP/?hl=de' },
    { platform: 'YouTube Short', url: 'https://youtube.com/shorts/j9lESyNTITI' },
  ];

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  if (!isExpanded) {
    return (
      <Tooltip title="Admin URLs" placement="left">
        <IconButton
          className={classes.toggleButton}
          onClick={() => setIsExpanded(true)}
          size="small">
          <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
            ADMIN
          </Typography>
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box className={`${classes.adminBox} ${className || ''}`}>
      <Typography className={classes.adminTitle}>Admin URLs</Typography>
      {urls.map(({ platform, url }) => (
        <Button key={platform} className={classes.urlItem} onClick={() => copyToClipboard(url)}>
          <Typography className={classes.urlText} title={url}>
            {platform}: {url}
          </Typography>
          <Tooltip title="Copy URL" placement="top">
            <IconButton className={classes.copyButton} size="small">
              📋
            </IconButton>
          </Tooltip>
        </Button>
      ))}
      <IconButton
        className={classes.copyButton}
        onClick={() => setIsExpanded(false)}
        size="small"
        sx={{ width: '100%', marginTop: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
          ✕
        </Typography>
      </IconButton>
    </Box>
  );
};

export default AdminUrlBox;
